/**
 * LicenceOverview - every licence the organisation holds, in one table
 * (R4-16, Settings › Billing & plan).
 *
 * "How can I see what one licence has?" The page showed the plan as a banner
 * and the add-ons as catalogue cards; who held which licence was two modals
 * deep, and what each one CONTAINS was only on the catalogue. One row per
 * licence now - the base plan and each add-on - with:
 *
 *   - what it contains: its modules grouped as the sidebar groups them, and
 *     its AI features (from the licence's own feature gates),
 *   - who it is assigned to,
 *   - its seats: a base plan has no cap (assigning someone adds a seat and
 *     billing follows); an add-on has the seats that were bought,
 *   - Manage people (assign / revoke) and, for a per-seat add-on, Change seats.
 *
 * The data is GET /licensing/assignments; the actions are the page's own
 * (AddonAssignmentModal, AddSeatsModal), so this file only presents.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiCaretDownLight, PiCaretRightLight, PiUserPlusLight, PiUsersThreeLight, PiSparkleLight } from 'react-icons/pi';
import clsx from 'clsx';
import Badge from '../TailwindComponents/Badge';
import Button from '../TailwindComponents/Button';
import { Well } from '../brand/Field';
import { AddonAssignment, AssignmentsResponse } from '../../../store/actions/PlanActions';
import { aiFeatureLabel, groupSections, sectionLabel } from './planCatalogue';

interface Props {
  data: AssignmentsResponse;
  onManagePeople: (licence: AddonAssignment) => void;
  /** Per-seat add-ons only. */
  onChangeSeats?: (licence: AddonAssignment) => void;
}

const NAMES_SHOWN = 3;

const LicenceOverview: React.FC<Props> = ({ data, onManagePeople, onChangeSeats }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<Set<number>>(new Set());

  const licences: Array<AddonAssignment & { isBase: boolean }> = [
    ...(data.base_plan ? [{ ...data.base_plan, isBase: true }] : []),
    ...data.addons.map((a) => ({ ...a, isBase: false })),
  ];

  if (licences.length === 0) {
    return <p className="text-sm text-fg-muted">{t('No active licences.')}</p>;
  }

  const toggle = (id: number) => setOpen((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  return (
    <div className="divide-y divide-hairline-soft overflow-hidden rounded-lg border border-hairline-soft" data-testid="licence-overview">
      {licences.map((lic) => {
        const sections = lic.feature_gates?.sections ?? [];
        const ai = lic.feature_gates?.ai_features ?? [];
        const groups = groupSections(sections);
        const users = lic.assigned_users || [];
        const expanded = open.has(lic.org_license_id);
        const full = !lic.isBase && lic.available_seats === 0;
        const canChangeSeats = !lic.isBase && !!onChangeSeats && (lic.billing_model ?? 'per_seat') === 'per_seat';
        return (
          <div key={lic.org_license_id} data-testid={`licence-${lic.org_license_id}`}>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
              <button
                type="button"
                onClick={() => toggle(lic.org_license_id)}
                aria-expanded={expanded}
                className="flex min-w-[12rem] flex-1 items-center gap-2 text-start"
              >
                {expanded
                  ? <PiCaretDownLight className="h-3.5 w-3.5 flex-shrink-0 text-fg-muted" />
                  : <PiCaretRightLight className="h-3.5 w-3.5 flex-shrink-0 text-fg-muted rtl:-scale-x-100" />}
                <span className="truncate text-sm font-medium text-fg-strong">{lic.display_name || lic.name}</span>
                <Badge color={lic.isBase ? 'blue' : 'gray'}>{lic.isBase ? t('Base plan') : t('Add-on')}</Badge>
              </button>

              <span className="w-40 flex-shrink-0 text-xs text-fg-muted">
                {t('{{n}} modules', { n: sections.length })}
                {ai.length > 0 && <> · {t('{{n}} AI features', { n: ai.length })}</>}
              </span>

              <span className="w-56 min-w-0 flex-shrink-0 truncate text-xs text-fg-body" title={users.map((u) => u.name).join(', ')}>
                {users.length === 0
                  ? <span className="text-fg-muted">{t('No one yet')}</span>
                  : <>
                    {users.slice(0, NAMES_SHOWN).map((u) => u.name).join(', ')}
                    {users.length > NAMES_SHOWN && <span className="text-fg-muted"> {t('+{{n}} more', { n: users.length - NAMES_SHOWN })}</span>}
                  </>}
              </span>

              <span className="w-36 flex-shrink-0">
                {lic.isBase ? (
                  <span className="text-xs text-fg-muted" title={t('Assigning someone adds a seat; billing follows.')}>
                    {t('{{n}} assigned', { n: lic.assigned_count })}
                  </span>
                ) : (
                  <Badge color={full ? 'yellow' : 'gray'}>
                    {t('{{used}} of {{total}} seats', { used: lic.assigned_count, total: lic.quantity })}
                  </Badge>
                )}
              </span>

              <span className="flex flex-shrink-0 items-center gap-2">
                <Button variant="outline" size="small" icon={<PiUsersThreeLight className="h-3.5 w-3.5" />} onClick={() => onManagePeople(lic)}>
                  {t('Manage people')}
                </Button>
                {canChangeSeats && (
                  <Button variant="neutral" size="small" icon={<PiUserPlusLight className="h-3.5 w-3.5" />} onClick={() => onChangeSeats!(lic)}>
                    {t('Change seats')}
                  </Button>
                )}
              </span>
            </div>

            {expanded && (
              <div className="space-y-3 px-4 pb-4 ps-10" data-testid={`licence-contents-${lic.org_license_id}`}>
                {lic.description && <p className="text-xs text-fg-muted">{lic.description}</p>}
                <Well className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {groups.map((g) => (
                    <div key={g.label}>
                      <p className="mb-1 text-[11px] font-medium text-fg-muted">{t(g.label)}</p>
                      <ul className="space-y-0.5">
                        {g.keys.map((k) => <li key={k} className="text-xs text-fg-body">{t(sectionLabel(k))}</li>)}
                      </ul>
                    </div>
                  ))}
                  {groups.length === 0 && <p className="text-xs text-fg-muted">{t('No modules listed for this licence.')}</p>}
                </Well>
                {ai.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <PiSparkleLight className="h-3.5 w-3.5 text-champagne" aria-hidden />
                    {ai.map((f) => <Badge key={f} color="gray">{t(aiFeatureLabel(f))}</Badge>)}
                  </div>
                )}
                {users.length > 0 && (
                  <div>
                    <p className="mb-1 text-[11px] font-medium text-fg-muted">{t('Assigned to')}</p>
                    <ul className="flex flex-wrap gap-1.5">
                      {users.map((u) => (
                        <li key={u.user_id} className={clsx('rounded border border-hairline-soft bg-surface px-2 py-0.5 text-xs text-fg-body')} title={u.email}>
                          {u.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default LicenceOverview;
